import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, ToggleButton, ToggleButtonGroup, Alert, Button, CircularProgress,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Forum as SessionsIcon,
  Chat as ChatIcon,
  Token as TokenIcon,
  AttachMoney as CostIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import aiAssistantService from '../../services/ai/aiAssistant.service';
import { getBrandColor, getBrandedTableHeadSx } from '../../utils/colorUtils';

const PERIODS = [7, 30, 90];

const formatTokens = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);
const formatCost = (amount) => `$${Number(amount || 0).toFixed(2)}`;
const errorText = (err) => err?.response?.data?.error || err?.response?.data?.message || 'Could not load AI usage';

// The daily endpoint only returns days with activity; fill the gaps so the chart shows the whole period
const fillDays = (rows, from, to) => {
  if (!from || !to) return [];
  const byDate = new Map((rows || []).map(r => [r.date, r]));
  const days = [];
  for (let d = new Date(`${from}T00:00:00`); d <= new Date(`${to}T00:00:00`); d.setDate(d.getDate() + 1)) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const r = byDate.get(key);
    days.push({
      date: key,
      label: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      exchanges: r ? r.exchanges : 0,
      tokens: r ? r.tokens_input + r.tokens_output : 0,
      cost: r ? Number(r.estimated_cost) : 0,
    });
  }
  return days;
};

const AiUsage = () => {
  const brandColor = getBrandColor();
  const headSx = getBrandedTableHeadSx();

  const [period, setPeriod] = useState(30);
  const [usage, setUsage] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summary, days] = await Promise.all([
          aiAssistantService.getUsage(period),
          aiAssistantService.getDailyUsage(period),
        ]);
        if (!cancelled) {
          setUsage(summary);
          setDaily(days || []);
        }
      } catch (err) {
        if (!cancelled) setError(errorText(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [period, reload]);

  const chartData = useMemo(() => fillDays(daily, usage?.period?.from, usage?.period?.to), [daily, usage]);

  const tokens = usage ? usage.total_tokens.input + usage.total_tokens.output : 0;
  const cards = usage ? [
    { label: 'Sessions', value: usage.total_sessions, icon: <SessionsIcon /> },
    { label: 'Messages', value: usage.total_exchanges, icon: <ChatIcon /> },
    { label: 'Tokens', value: formatTokens(tokens), icon: <TokenIcon /> },
    { label: 'Est. cost', value: formatCost(usage.estimated_cost.amount), icon: <CostIcon /> },
  ] : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon sx={{ color: brandColor }} />
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>AI Overview</Typography>
          {usage?.period && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}>
              {usage.period.from} – {usage.period.to}
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(e, v) => v && setPeriod(v)}
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', px: 1.5, py: 0.25 },
            '& .Mui-selected': { color: `${brandColor} !important`, bgcolor: `${brandColor}14 !important`, borderColor: `${brandColor} !important` },
          }}
        >
          {PERIODS.map(d => <ToggleButton key={d} value={d}>{d} days</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={() => setReload(r => r + 1)}>Retry</Button>}>
          {error}
        </Alert>
      )}
      {loading && !usage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      )}

      {usage && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {cards.map(c => (
              <Grid item xs={6} md={3} key={c.label}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
                    <Box sx={{ color: brandColor, mb: 0.5 }}>{c.icon}</Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>{c.value}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{c.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1.5 }}>Messages per day</Typography>
            {tokens === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', py: 4, textAlign: 'center' }}>
                No AI Assistant use in this period
              </Typography>
            ) : (
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name, item) => (name === 'exchanges'
                        ? [`${value} messages · ${formatTokens(item.payload.tokens)} tokens · ${formatCost(item.payload.cost)}`, '']
                        : [value, name])}
                      labelStyle={{ fontSize: 12 }}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Bar dataKey="exchanges" fill={brandColor} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Paper sx={{ overflow: 'hidden' }}>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, p: 2, pb: 1 }}>Usage by team member</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={headSx}>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>User</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Sessions</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Input tokens</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Output tokens</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Est. cost</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Last used</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(usage.by_user || []).map(u => (
                    <TableRow key={u.user_id} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{u.user_name || 'Unknown user'}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{u.sessions}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{formatTokens(u.tokens_input)}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{formatTokens(u.tokens_output)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCost(u.estimated_cost)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {u.last_used ? new Date(u.last_used).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(usage.by_user || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, fontSize: '0.8rem', color: 'text.secondary' }}>
                        No one has used the AI Assistant in this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
            Costs are estimates from the AI provider's list prices (USD); your provider invoice is the final figure.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AiUsage;
